import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { X, Filter, ChevronUp, ChevronDown, Settings2, RefreshCw, Check, Loader2 } from 'lucide-react';
import { SITE_FLAGS } from '../shared';
import { useCan } from '../../../lib/rbac';

function TagInput({ value, onChange, placeholder, presets, readOnly }: { value: string[]; onChange: (v: string[]) => void; placeholder: string; presets?: string[]; readOnly?: boolean }) {
  const [input, setInput] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      if (!value.includes(input.trim())) {
        onChange([...value, input.trim()]);
      }
      setInput('');
    }
  };

  const addPreset = (preset: string) => {
    if (!value.includes(preset)) onChange([...value, preset]);
  };

  const remove = (tag: string) => onChange(value.filter(v => v !== tag));

  const availablePresets = presets?.filter(p => !value.includes(p)) || [];

  return (
    <div>
      <div className="flex flex-wrap gap-1 items-center px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded min-h-[32px]">
        {value.map(tag => (
          <span key={tag} className="flex items-center gap-1 px-1.5 py-0.5 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded text-xs">
            {tag}
            {!readOnly && <button onClick={() => remove(tag)} className="hover:text-red-400"><X size={10} /></button>}
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={readOnly}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[60px] bg-transparent text-xs text-[var(--color-text)] outline-none"
        />
      </div>
      {!readOnly && availablePresets.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {availablePresets.map(p => (
            <button
              key={p}
              onClick={() => addPreset(p)}
              className="px-1.5 py-0.5 text-[10px] rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors"
            >
              + {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Feeder Filters Panel ───

const ALL_SITES = ['MLA', 'MLM', 'MLC', 'MCO', 'MLB'];

const DEFAULT_FILTERS: Record<string, any> = {
  current_SALES_lte: 100000,
  avg90_SALES_lte: 100000,
  current_NEW_gte: 1000,
  current_NEW_lte: 20000,
  minRating: 40,
  maxAsins: 200,
  monthlySold_gte: 50,
  packageWeight_lte: 5000,
  packageMaxSide_lte: 600,
  outOfStockPct90Amz_gte: 0,
  offerCountFBA_lte: 5,
  salesRankDrops30_gte: 0,
  maxDeliveryDays: null,
  excludePreorders: true,
  excludeAmazonSeller: true,
  // Familias (variantes de un mismo producto) y empates por nombre: apagado por defecto.
  includeVariants: false,
  current_NEW_FBA_gte: 0,
  itemCondition: 'new' as 'new' | 'refurbished',
  sites: ['MLA', 'MLM', 'MLC', 'MCO'],
  _enabled: {},
  adv_title: '',
  adv_material: '',
  adv_brand: [],
  adv_manufacturer: [],
  adv_color: [],
  adv_size: [],
  adv_languages: [],
  adv_binding: [],
  adv_author: [],
  adv_publisher: [],
  adv_genre: [],
  adv_numberOfPages_gte: null,
  adv_numberOfPages_lte: null,
  adv_numberOfItems_gte: null,
  adv_numberOfItems_lte: null,
  adv_productType: '',
  adv_hasReviews: false,
  adv_isSNS: false,
  adv_noHazMat: false,
  adv_hasAPlus: false,
  adv_isDeal: false,
  adv_categories_include: [],
  adv_categories_exclude: [],
};

function FeederFiltersPanel({ open, onToggle, onFiltersChange }: { open: boolean; onToggle: () => void; onFiltersChange?: (f: any) => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const { sellerId } = useUIStore();
  const [filters, setFilters] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    api.get<any>(`/publisher/filters?seller_id=${sellerId}`).then(res => {
      if (res.success && res.data) {
        const f = res.data.filters || {};
        setFilters(f);
        onFiltersChange?.(f);
      }
    }).catch(() => {});
  }, [sellerId]);

  // V1 parity: propagate every filter change to parent immediately (not just on Save)
  // In V1, filters state lived in FeederTab and was shared via setFilters prop.
  // Here we sync local state → parent on every change so DiscoverSubTab always has current filters.
  const filtersRef = useRef<any>(null);
  useEffect(() => {
    if (filters && filtersRef.current !== null) {
      // Skip the initial load (already handled above) — only propagate user edits
      onFiltersChange?.(filters);
    }
    filtersRef.current = filters;
  }, [filters]);

  const handleSave = async () => {
    if (!filters) return;
    setSaving(true);
    try {
      await api.put('/publisher/filters', { seller_id: sellerId, filters });
      onFiltersChange?.(filters);
    } catch (err) {
      console.error('Save filters failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFilters({ ...DEFAULT_FILTERS });
  };

  const f = filters || {};
  const enabled = f._enabled || {};

  const isEnabled = (field: string) => enabled[field] !== false;

  const toggleEnabled = (field: string) => {
    setFilters((prev: any) => ({
      ...prev,
      _enabled: { ...prev._enabled, [field]: !(prev._enabled || {})[field] === false ? false : undefined },
    }));
  };

  // Proper toggle: if currently enabled (not false), set to false. If currently false, remove key (=enabled)
  const toggleField = (field: string) => {
    const cur = (f._enabled || {})[field];
    const newEnabled = { ...(f._enabled || {}) };
    if (cur === false) {
      delete newEnabled[field];
    } else {
      newEnabled[field] = false;
    }
    setFilters((prev: any) => ({ ...prev, _enabled: newEnabled }));
  };

  const setF = (field: string, val: any) => {
    setFilters((prev: any) => ({ ...prev, [field]: val }));
  };

  // Number input as text — allows free typing, converts on blur
  const [numDraft, setNumDraft] = useState<Record<string, string>>({});
  const numProps = (field: string, opts?: { scale?: number; decimals?: number }) => {
    const scale = opts?.scale || 1;
    const dec = opts?.decimals ?? 0;
    const stored = (f as any)[field];
    const display = numDraft[field] ?? (stored != null ? (stored / scale).toFixed(dec) : '');
    return {
      type: 'text' as const,
      inputMode: (dec > 0 ? 'decimal' : 'numeric') as 'decimal' | 'numeric',
      value: display,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        // Allow empty, digits, minus, one dot
        if (raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw)) {
          setNumDraft(prev => ({ ...prev, [field]: raw }));
        }
      },
      onBlur: () => {
        const raw = numDraft[field];
        if (raw === undefined) return;
        const num = raw ? Number(raw) : null;
        setF(field, num != null && !isNaN(num) ? (dec > 0 ? Math.round(num * scale) : num) : null);
        setNumDraft(prev => { const n = { ...prev }; delete n[field]; return n; });
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      },
      disabled: !isEnabled(field) || !canSettings,
      className: inputCls(field),
    };
  };

  const inputCls = (field: string) =>
    `w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] ${!isEnabled(field) ? 'opacity-40' : ''}`;

  const renderToggle = (field: string) => (
    <button
      onClick={() => toggleField(field)}
      disabled={!canSettings}
      className={`w-8 h-4 rounded-full relative transition-colors ${isEnabled(field) ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'}`}
    >
      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${isEnabled(field) ? 'left-4' : 'left-0.5'}`} />
    </button>
  );

  const canMoney = can('global.view_money');
  // El panel se ve siempre en Descubrimiento; sin la llave de ajustes es de solo lectura.
  const canSettings = can('publisher.settings');
  const sites = f.sites || [];
  const toggleSite = (site: string) => {
    const cur = sites.includes(site);
    setF('sites', cur ? sites.filter((s: string) => s !== site) : [...sites, site]);
  };

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)]">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-[var(--color-text)]"
      >
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[var(--color-primary)]" />
          {t('pub.feed.filters')}
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && filters && (
        <div className="px-4 pb-4 border-t border-[var(--color-border)] pt-3 space-y-4">
          {/* Row 1: Sales Rank, Avg Rank, Price, Rating, Max ASINs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Sales Rank max</label>
                {renderToggle('current_SALES_lte')}
              </div>
              <input {...numProps('current_SALES_lte')} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Avg Rank 90d</label>
                {renderToggle('avg90_SALES_lte')}
              </div>
              <input {...numProps('avg90_SALES_lte')} />
            </div>
            {canMoney && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Min Price USD</label>
                {renderToggle('current_NEW_gte')}
              </div>
              <input {...numProps('current_NEW_gte', { scale: 100, decimals: 2 })} />
            </div>
            )}
            {canMoney && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Max Price USD</label>
                {renderToggle('current_NEW_lte')}
              </div>
              <input {...numProps('current_NEW_lte', { scale: 100, decimals: 2 })} />
            </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Min Rating</label>
                {renderToggle('minRating')}
              </div>
              <input {...numProps('minRating', { scale: 10, decimals: 1 })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Max ASINs</label>
                {renderToggle('maxAsins')}
              </div>
              <input {...numProps('maxAsins')} />
            </div>
          </div>

          {/* Row 2: Monthly Sold, Weight, Max Side, OOS % */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">{t('pub.feed.monthlySold')} min</label>
                {renderToggle('monthlySold_gte')}
              </div>
              <input {...numProps('monthlySold_gte')} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">{t('pub.feed.maxWeight')} (kg)</label>
                {renderToggle('packageWeight_lte')}
              </div>
              <input {...numProps('packageWeight_lte', { scale: 1000, decimals: 1 })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Max Side (cm)</label>
                {renderToggle('packageMaxSide_lte')}
              </div>
              <input {...numProps('packageMaxSide_lte', { scale: 10, decimals: 1 })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">AMZ OOS 90d %</label>
                {renderToggle('outOfStockPct90Amz_gte')}
              </div>
              <input {...numProps('outOfStockPct90Amz_gte')} />
            </div>
          </div>

          {/* Row 3: FBA Sellers, Sales Drops, Max Delivery */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">{t('pub.feed.maxFBA')}</label>
                {renderToggle('offerCountFBA_lte')}
              </div>
              <input {...numProps('offerCountFBA_lte')} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Sales Drops 30d min</label>
                {renderToggle('salesRankDrops30_gte')}
              </div>
              <input {...numProps('salesRankDrops30_gte')} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-[var(--color-text-tertiary)]">Max Delivery Days</label>
                {renderToggle('maxDeliveryDays')}
              </div>
              <input {...numProps('maxDeliveryDays')} />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={(f.current_NEW_FBA_gte ?? 0) >= 1}
                onChange={e => setF('current_NEW_FBA_gte', e.target.checked ? 1 : 0)}
                disabled={!canSettings}
                className="rounded border-[var(--color-border)]"
              />
              Prime/FBA only
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={f.excludeAmazonSeller || false}
                onChange={e => setF('excludeAmazonSeller', e.target.checked)}
                disabled={!canSettings}
                className="rounded border-[var(--color-border)]"
              />
              {t('pub.feed.excludeAmazon')}
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={f.excludePreorders || false}
                onChange={e => setF('excludePreorders', e.target.checked)}
                disabled={!canSettings}
                className="rounded border-[var(--color-border)]"
              />
              {t('pub.feed.excludePreorders')}
            </label>
            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={f.includeVariants || false}
                onChange={e => setF('includeVariants', e.target.checked)}
                disabled={!canSettings}
                className="rounded border-[var(--color-border)]"
              />
              {t('pub.feed.includeVariants')}
            </label>
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[var(--color-border)]">
              <span className="text-xs text-[var(--color-text-tertiary)] mr-1">Condition:</span>
              {(['new', 'refurbished'] as const).map(c => (
                <button
                  key={c}
                  onClick={() => setF('itemCondition', c)}
                  disabled={!canSettings}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    (f.itemCondition || 'new') === c
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  {c === 'new' ? 'New' : 'Refurbished'}
                </button>
              ))}
            </div>
          </div>

          {/* Sites Selector */}
          <div>
            <label className="text-xs text-[var(--color-text-tertiary)] block mb-1.5">Sites</label>
            <div className="flex flex-wrap gap-2">
              {ALL_SITES.map(site => (
                <button
                  key={site}
                  onClick={() => toggleSite(site)}
                  disabled={!canSettings}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    sites.includes(site)
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-[var(--color-bg)] text-[var(--color-text-tertiary)] border border-[var(--color-border)]'
                  }`}
                >
                  <span>{SITE_FLAGS[site] || ''}</span>
                  {site}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Filters (collapsible) */}
          <div>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] font-medium"
            >
              <Settings2 size={12} />
              Advanced Filters
              {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-4 p-3 bg-[var(--color-bg-tertiary)] rounded-lg border border-[var(--color-border)]">
                {/* Text search */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Title keywords</label>
                    <input
                      type="text"
                      value={f.adv_title || ''}
                      onChange={e => setF('adv_title', e.target.value)}
                      disabled={!canSettings}
                      placeholder="e.g. wireless bluetooth"
                      className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Material</label>
                    <input
                      type="text"
                      value={f.adv_material || ''}
                      onChange={e => setF('adv_material', e.target.value)}
                      disabled={!canSettings}
                      placeholder="e.g. leather"
                      className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Tag inputs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Brand</label>
                    <TagInput value={f.adv_brand || []} onChange={v => setF('adv_brand', v)} placeholder="Add brand..." presets={['DeWalt','Makita','Milwaukee','OXO','KitchenAid','Samsung','Apple','Sony','Anker','Logitech','Nike','Adidas']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Manufacturer</label>
                    <TagInput value={f.adv_manufacturer || []} onChange={v => setF('adv_manufacturer', v)} placeholder="Add manufacturer..." presets={['Apple Inc.','Samsung Electronics','Sony Corporation','LG Electronics','Procter & Gamble','3M']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Color</label>
                    <TagInput value={f.adv_color || []} onChange={v => setF('adv_color', v)} placeholder="Add color..." presets={['Black','White','Blue','Red','Silver','Gray','Pink','Green','Gold','Navy']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Size</label>
                    <TagInput value={f.adv_size || []} onChange={v => setF('adv_size', v)} placeholder="Add size..." presets={['Small','Medium','Large','XL','XXL','One Size']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Languages</label>
                    <TagInput value={f.adv_languages || []} onChange={v => setF('adv_languages', v)} placeholder="Add language..." presets={['English','Spanish','Portuguese','French','German','Italian','Japanese','Chinese','Korean']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Binding</label>
                    <TagInput value={f.adv_binding || []} onChange={v => setF('adv_binding', v)} placeholder="Add binding..." presets={['lp_record','audioCD','Paperback','Hardcover','Spiral-bound','Board book','Mass Market Paperback','Library Binding']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Author</label>
                    <TagInput value={f.adv_author || []} onChange={v => setF('adv_author', v)} placeholder="Add author..." readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Publisher</label>
                    <TagInput value={f.adv_publisher || []} onChange={v => setF('adv_publisher', v)} placeholder="Add publisher..." presets={['Penguin','HarperCollins','Simon & Schuster','Random House','Hachette','Macmillan','Scholastic','Wiley']} readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Genre</label>
                    <TagInput value={f.adv_genre || []} onChange={v => setF('adv_genre', v)} placeholder="Add genre..." presets={['Fiction','Non-Fiction','Self-Help','Romance','Mystery','Science Fiction','Fantasy','Biography','History','Business','Children','Comics']} readOnly={!canSettings} />
                  </div>
                </div>

                {/* Number fields */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Pages min</label>
                    <input {...{...numProps('adv_numberOfPages_gte'), disabled: !canSettings, className: inputCls('_always')}} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Pages max</label>
                    <input {...{...numProps('adv_numberOfPages_lte'), disabled: !canSettings, className: inputCls('_always')}} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Items in pack min</label>
                    <input {...{...numProps('adv_numberOfItems_gte'), disabled: !canSettings, className: inputCls('_always')}} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Items in pack max</label>
                    <input {...{...numProps('adv_numberOfItems_lte'), disabled: !canSettings, className: inputCls('_always')}} />
                  </div>
                </div>

                {/* Product type dropdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Product Type</label>
                    <select
                      value={f.adv_productType ?? ''}
                      onChange={e => setF('adv_productType', e.target.value)}
                      disabled={!canSettings}
                      className="w-full px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                    >
                      <option value="">Any</option>
                      <option value="0">Standard</option>
                      <option value="1">Downloadable</option>
                      <option value="2">eBook</option>
                    </select>
                  </div>
                </div>

                {/* Advanced checkboxes */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={f.adv_hasReviews || false} onChange={e => setF('adv_hasReviews', e.target.checked)} disabled={!canSettings} className="rounded border-[var(--color-border)]" />
                    Has Reviews
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={f.adv_isSNS || false} onChange={e => setF('adv_isSNS', e.target.checked)} disabled={!canSettings} className="rounded border-[var(--color-border)]" />
                    Subscribe & Save
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={f.adv_noHazMat || false} onChange={e => setF('adv_noHazMat', e.target.checked)} disabled={!canSettings} className="rounded border-[var(--color-border)]" />
                    No HazMat
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={f.adv_hasAPlus || false} onChange={e => setF('adv_hasAPlus', e.target.checked)} disabled={!canSettings} className="rounded border-[var(--color-border)]" />
                    A+ Content
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={f.adv_isDeal || false} onChange={e => setF('adv_isDeal', e.target.checked)} disabled={!canSettings} className="rounded border-[var(--color-border)]" />
                    Current Deal
                  </label>
                </div>

                {/* Category IDs include/exclude */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Category IDs (include)</label>
                    <TagInput value={(f.adv_categories_include || []).map(String)} onChange={v => setF('adv_categories_include', v.map(Number).filter(n => !isNaN(n)))} placeholder="Add category ID..." readOnly={!canSettings} />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">Category IDs (exclude)</label>
                    <TagInput value={(f.adv_categories_exclude || []).map(String)} onChange={v => setF('adv_categories_exclude', v.map(Number).filter(n => !isNaN(n)))} placeholder="Add category ID..." readOnly={!canSettings} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons — guardar y restaurar piden la llave de ajustes */}
          {canSettings && (
          <div className="flex justify-between items-center">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--color-border)] rounded text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
            >
              <RefreshCw size={12} />
              Reset defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded text-xs font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {t('pub.feed.saveFilters')}
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

export { FeederFiltersPanel };
